import { useState, useEffect, useMemo, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { captureError } from "@/lib/errorReporting";
import { Icon } from "@/components/ui";
import Button from "@/components/ui/Button";
import { useAuth, useConfig } from "@/providers";
import {
    estimateTokensForDeposit,
    formatTokenAmount,
    getCurveParams,
} from "@/utils/curveEstimator";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8080";

interface CabalSummary {
    _id: string;
    name: string;
    ticker: string;
    slug: string;
    imageUrl?: string;
    status: "recruiting" | "launched" | "failed";
    isGraduated?: boolean;
    depositTarget: number;
    depositCurrent: number;
    targetMcap: number;
    memberCount: number;
    leaderDeposit: number;
    recruitingDeadline?: string;
}

/* ════════════════════════════════════════════
   My Active Cabals Page
   ════════════════════════════════════════════ */
export default function MyCabalsPage(): ReactNode {
    const navigate = useNavigate();
    const { token } = useAuth();
    const [cabals, setCabals] = useState<CabalSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [stakesLoading, setStakesLoading] = useState(!!token);
    const [stakes, setStakes] = useState<Record<string, { sol: number; estimatedTokens: number; actualTokens?: string }>>({});
    const [showFailed, setShowFailed] = useState(false);

    // Fetch all cabals
    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);

        fetch(`${API_BASE}/api/cabals`, { signal: controller.signal })
            .then((r) => {
                if (!r.ok) throw new Error("Failed to fetch");
                return r.json();
            })
            .then((data) => setCabals(data))
            .catch((err) => {
                if (err.name !== "AbortError") captureError("Fetch error:", err);
            })
            .finally(() => setLoading(false));

        return () => controller.abort();
    }, []);

    // Fetch user's stakes (only if logged in)
    useEffect(() => {
        if (!token) {
            setStakes({});
            setStakesLoading(false);
            return;
        }

        setStakesLoading(true);
        fetch(`${API_BASE}/api/cabals/my-stakes`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((r) => (r.ok ? r.json() : { stakes: {} }))
            .then((data) => setStakes(data.stakes || {}))
            .catch(() => setStakes({}))
            .finally(() => setStakesLoading(false));
    }, [token]);

    const filtered = cabals.filter((c) => {
        if (!stakes[c.slug]) return false;
        if (c.status === "failed") return showFailed;
        if (stakes[c.slug].sol <= 0) return false;
        return true;
    });

    const failedCount = cabals.filter((c) => stakes[c.slug] && c.status === "failed").length;

    return (
        <div className="relative flex min-h-[100dvh] w-full flex-col overflow-hidden max-w-[430px] mx-auto bg-[#0a0a0a]">
            <main className="relative z-10 flex-1 flex flex-col px-[clamp(16px,2.6dvh,24px)] pt-[clamp(44px,6dvh,56px)] pb-[clamp(140px,18dvh,180px)]">
                {/* ─── Header ─── */}
                <div className="flex items-center justify-between mb-[clamp(16px,2.6dvh,24px)]">
                    <h1 className="text-[clamp(34px,4.5dvh,42px)] font-bold leading-[1.15] text-white tracking-[-0.84px]">
                        My<br />Codevs
                    </h1>
                    <Icon name="codev-logo" size={66} className="!w-[clamp(52px,7.1dvh,66px)] !h-[clamp(52px,7.1dvh,66px)]" />
                </div>

                {/* ─── Show Failed Toggle ─── */}
                {failedCount > 0 && (
                    <button
                        onClick={() => setShowFailed((v) => !v)}
                        className="flex items-center gap-[8px] mb-[16px] self-start"
                    >
                        <div className={`w-[36px] h-[20px] rounded-full p-[2px] transition-colors ${showFailed ? "bg-[#f3ce28]" : "bg-white/15"}`}>
                            <div className={`size-[16px] rounded-full bg-white transition-transform ${showFailed ? "translate-x-[16px]" : "translate-x-0"}`} />
                        </div>
                        <span className="text-[13px] font-medium text-white/50">
                            Show failed ({failedCount})
                        </span>
                    </button>
                )}

                {/* ─── List ─── */}
                {loading || stakesLoading ? (
                    <div className="flex flex-col gap-[14px]">
                        {[1, 2, 3].map((i) => (
                            <div
                                key={i}
                                className="w-full rounded-[12px] p-[16px] flex flex-col gap-[20px] animate-pulse"
                                style={{
                                    background: "linear-gradient(to bottom, rgba(115,115,115,0), rgba(115,115,115,0.2))",
                                    border: "0.5px solid rgba(153,153,153,0.5)",
                                }}
                            >
                                {/* Top: avatar + ticker + badge */}
                                <div className="flex items-center justify-between">
                                    <div className="flex gap-[12px] items-center">
                                        <div className="size-[48px] rounded-full bg-white/5" />
                                        <div className="h-5 w-16 bg-white/5 rounded" />
                                    </div>
                                    <div className="h-6 w-16 bg-white/5 rounded-[4px]" />
                                </div>
                                {/* Middle: SOL + progress bar */}
                                <div className="flex flex-col gap-[16px]">
                                    <div className="flex justify-between">
                                        <div className="h-3.5 w-28 bg-white/5 rounded" />
                                        <div className="h-3.5 w-10 bg-white/5 rounded" />
                                    </div>
                                    <div className="h-[8px] w-full bg-white/[0.08] rounded-[16px]" />
                                </div>
                                {/* Bottom: two stats */}
                                <div className="flex gap-[16px]">
                                    <div className="flex-1 flex flex-col gap-[6px]">
                                        <div className="h-3 w-20 bg-white/5 rounded" />
                                        <div className="h-4 w-12 bg-white/5 rounded" />
                                    </div>
                                    <div className="flex-1 flex flex-col gap-[6px]">
                                        <div className="h-3 w-20 bg-white/5 rounded" />
                                        <div className="h-4 w-16 bg-white/5 rounded" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : filtered.length === 0 ? (
                    <EmptyState onCreateClick={() => navigate("/create")} />
                ) : (
                    <div className="flex flex-col gap-[14px]">
                        {filtered.map((cabal) => (
                            <CabalCard
                                key={cabal._id}
                                cabal={cabal}
                                stakeData={stakes[cabal.slug]}
                                onManage={() => navigate(`/codevs/${cabal.slug}`)}
                            />
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}

/* ─── Cabal Summary Card ─── */
function CabalCard({
    cabal,
    stakeData,
    onManage,
}: {
    cabal: CabalSummary;
    stakeData?: { sol: number; estimatedTokens: number; actualTokens?: string };
    onManage: () => void;
}): ReactNode {
    const progress = cabal.depositTarget > 0
        ? Math.min((cabal.depositCurrent / cabal.depositTarget) * 100, 100)
        : 0;

    const isLaunched = cabal.status === "launched";
    const userStake = stakeData?.sol ?? 0;

    // Stake weight = user's % of pool
    const stakeWeight = cabal.depositTarget > 0 && userStake > 0
        ? ((userStake / cabal.depositTarget) * 100).toFixed(2)
        : "0.00";

    // Token allocation display
    const { INITIAL_MC_SOL, MIGRATION_MC_SOL, TOTAL_TOKEN_SUPPLY } = useConfig();
    const curveParams = useMemo(() => getCurveParams(INITIAL_MC_SOL, MIGRATION_MC_SOL, TOTAL_TOKEN_SUPPLY), [INITIAL_MC_SOL, MIGRATION_MC_SOL, TOTAL_TOKEN_SUPPLY]);
    const { allocationDisplay, allocationLabel } = useMemo(() => {
        if (userStake <= 0) return { allocationDisplay: "—", allocationLabel: "Est. Allocation" };

        if (isLaunched && stakeData?.actualTokens) {
            const actual = Number(stakeData.actualTokens) / 1e9;
            return {
                allocationDisplay: formatTokenAmount(actual),
                allocationLabel: "Allocation",
            };
        }

        if (stakeData?.estimatedTokens && stakeData.estimatedTokens > 0) {
            return {
                allocationDisplay: formatTokenAmount(stakeData.estimatedTokens),
                allocationLabel: "Est. Allocation",
            };
        }

        const { tokensOut } = estimateTokensForDeposit(userStake, curveParams.sqrtStartPrice, curveParams);
        return {
            allocationDisplay: formatTokenAmount(tokensOut),
            allocationLabel: "Est. Allocation",
        };
    }, [userStake, isLaunched, stakeData, curveParams]);

    return (
        <button
            onClick={onManage}
            className="w-full text-left bg-gradient-to-b from-[rgba(115,115,115,0)] to-[rgba(115,115,115,0.2)] border-[0.5px] border-[rgba(153,153,153,0.5)] rounded-[12px] p-[16px] flex flex-col gap-[20px]"
        >
            {/* Top row: avatar + ticker + LIVE badge */}
            <div className="flex items-center justify-between w-full">
                <div className="flex gap-[12px] items-center">
                    {/* Token avatar with gradient ring */}
                    <div className="bg-gradient-to-b from-[#2e241d] to-[#1e1b19] rounded-full p-[3px]">
                        <div className="size-[42px] rounded-full overflow-hidden">
                            {cabal.imageUrl ? (
                                <img
                                    alt={cabal.ticker}
                                    className="w-full h-full object-contain"
                                    src={cabal.imageUrl}
                                />
                            ) : (
                                <div className="w-full h-full bg-[#0a0a0a] flex items-center justify-center">
                                    <span className="text-[16px] font-bold text-white">
                                        {cabal.ticker.toUpperCase().charAt(0)}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                    <p className="font-medium text-[clamp(22px,2.8dvh,26px)] text-white tracking-[-0.52px] leading-[1.13] truncate max-w-[200px]">
                        {cabal.ticker.toUpperCase()}
                    </p>
                </div>
                <div className="bg-[rgba(255,255,255,0.09)] flex items-center px-[10px] py-[4px] rounded-[4px]">
                    <span className="font-medium text-[12px] text-white leading-[1.15]">
                        {cabal.status === "failed" ? "FAILED" : cabal.status === "launched" ? (cabal.isGraduated ? "GRADUATED" : "LAUNCHED") : "LIVE"}
                    </span>
                </div>
            </div>

            {/* Middle: SOL amount + percentage + progress bar */}
            <div className="flex flex-col gap-[16px] w-full">
                <div className="flex items-start justify-between w-full">
                    <p className="font-medium text-[14px] text-white tracking-[-0.28px] leading-[1.15]">
                        {parseFloat(cabal.depositCurrent.toFixed(2))} / {parseFloat(cabal.depositTarget.toFixed(2))} SOL
                    </p>
                    <p className="font-semibold text-[14px] text-[#f3ce28] leading-normal">
                        {Math.round(progress)}%
                    </p>
                </div>
                <div className="w-full h-[8px] bg-[rgba(255,255,255,0.08)] rounded-[16px] overflow-hidden">
                    <div
                        className="h-full bg-[#f3ce28] rounded-[16px] transition-all duration-500"
                        style={{ width: `${Math.max(progress, 1)}%` }}
                    />
                </div>
            </div>

            {/* Bottom: Stake Weight + Est. Allocation */}
            <div className="flex gap-[16px] items-start w-full">
                <div className="flex-1 flex flex-col gap-[6px]">
                    <p className="font-medium text-[14px] text-[rgba(255,255,255,0.4)] tracking-[-0.28px] leading-[1.15]">
                        Pool share
                    </p>
                    <p className="font-medium text-[16px] text-white tracking-[-0.32px] leading-[1.15]">
                        {stakeWeight} %
                    </p>
                </div>
                <div className="flex-1 flex flex-col gap-[6px]">
                    <p className="font-medium text-[14px] text-[rgba(255,255,255,0.4)] tracking-[-0.28px] leading-[1.15]">
                        {allocationLabel}
                    </p>
                    <div className="flex gap-[6px] items-baseline">
                        <p className="font-medium text-[16px] text-white tracking-[-0.32px] leading-[1.15]">
                            {allocationDisplay}
                        </p>
                        <p className="font-medium text-[12px] text-white tracking-[-0.24px] leading-[1.15] truncate">
                            {cabal.ticker.toUpperCase()}
                        </p>
                    </div>
                </div>
            </div>
        </button>
    );
}

/* ─── Empty State ─── */
function EmptyState({
    onCreateClick,
}: {
    onCreateClick: () => void;
}): ReactNode {
    return (
        <div className="flex-1 flex flex-col items-center justify-center gap-[24px]">
            <div className="size-[80px] rounded-full border-[0.5px] border-[rgba(153,153,153,0.5)] bg-gradient-to-b from-[rgba(115,115,115,0)] to-[rgba(115,115,115,0.1)] flex items-center justify-center">
                <Icon name="my-codevs" size={32} inline className="text-[#6E6975]" />
            </div>
            <div className="text-center flex flex-col gap-[8px]">
                <p className="text-white font-semibold text-[clamp(18px,2.1dvh,20px)]">
                    No Codevs yet
                </p>
                <p className="text-[rgba(255,255,255,0.4)] text-[14px] font-medium leading-[1.4] max-w-[280px] mx-auto">
                    Create or join a codev to start pooling SOL and launching tokens
                </p>
            </div>
            <Button
                variant="cta"
                size="inline"
                textStyle="v2"
                onClick={onCreateClick}
            >
                Codev a coin
            </Button>
        </div>
    );
}
